import React from 'react'
import { StyledTabsList, TabItem } from '@ironscales/design-system'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { Tabs as BaseTabs } from '@base-ui/react/tabs'

/**
 * PageTabs — a routed, in-page horizontal tab strip.
 *
 * Replaces the old per-page secondary sidebars (IC-48828) under the new platform
 * navigation. Unlike DesignSystem Tabs (which render content panels), PageTabs is
 * a NAV STRIP ONLY: it composes the V3 Tabs primitives (Root + List + tab items)
 * with NO panel — the page keeps rendering its own routed content via its existing
 * <Switch>. Each tab keeps its own URL, so deep links / back-forward work unchanged.
 *
 * Props
 * -----
 * id     {string}   Unique id for the tablist (a11y + scroll anchoring).
 * tabs   {Array}    [{ value: route, label, badge?, match?, search?, visible?, disabled? }]
 *                   - value:   the route to navigate to (also the tab's value)
 *                   - label:   string (or the V3 {icon,title} object)
 *                   - badge:   optional count rendered as "Label · n"
 *                   - match:   optional path prefix, or a list of prefixes, for active
 *                              detection (default: value). Navigation still goes to value.
 *                   - search:  optional query string (`''` or `'?foo=bar'`). When set,
 *                              the tab is active only if the current search matches,
 *                              so two tabs can share one pathname.
 *                   - visible: set false to omit the tab
 *                   - disabled: set true to render the tab inert
 * variant {'bordered'|'boxed'}  Default 'bordered' (underline page tabs).
 * size    {'sm'|'md'|'lg'}      Default 'md'.
 */
const Root = styled(BaseTabs.Root)`
  width: 100%;
  font-family: Poppins, sans-serif;

  /* Hover across the new nav chrome is pale blue (rail rows, settings rows,
     profile items — see navStyles.js). The shared V3 TabItem defaults to the
     generic grey --color-elevated-mark, which reads washed-out against the
     tinted page background. Scoped here rather than changed in TabItem so the
     shared DS primitive — and its other consumer, credit management — is
     untouched. Both tokens are theme-aware, so this holds in dark mode. */
  [role='tab']:hover:not([aria-disabled='true']):not([aria-selected='true']) {
    background: var(--sidebar-item-hover-background);
  }
`

const normalizeSearch = (search) => {
  if (!search) return ''
  return search.startsWith('?') ? search : `?${search}`
}

const tabRoutes = (tab) => {
  const raw = tab.match || String(tab.value).split('?')[0]
  const list = Array.isArray(raw) ? raw : [raw]
  return list.map((route) => String(route).split('?')[0])
}

// Clicking a tab always opens its value. `match` only decides which tab looks selected.
const tabNavigatePath = (tab) => {
  if (Array.isArray(tab.match)) return String(tab.value).split('?')[0]
  return tab.match || String(tab.value).split('?')[0]
}

// Active tab = the visible tab whose route is the longest prefix of the current
// pathname (exact or `${route}/...`). When a tab sets `search`, it must also
// match the current query. Returns the matched tab's value (string) or
// undefined when no tab matches the current route.
const resolveActiveValue = (tabs, pathname, search) => {
  const currentSearch = normalizeSearch(search)
  let best
  let bestLen = -1
  tabs.forEach((tab) => {
    const matched = tabRoutes(tab).find(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    )
    if (!matched) return
    if (tab.search !== undefined && normalizeSearch(tab.search) !== currentSearch) return
    if (matched.length > bestLen) {
      best = tab
      bestLen = matched.length
    }
  })
  return best ? String(best.value) : undefined
}

const PageTabs = ({ id, tabs, variant = 'bordered', size = 'sm', className }) => {
  const history = useHistory()
  const { pathname, search } = useLocation()

  const visibleTabs = tabs.filter((tab) => tab.visible !== false)
  const activeValue = resolveActiveValue(visibleTabs, pathname, search)

  const handleValueChange = (nextValue) => {
    if (!nextValue || nextValue === activeValue) return
    const tab = visibleTabs.find((item) => String(item.value) === String(nextValue))
    if (!tab) return
    if (tab.search !== undefined) {
      history.push({
        pathname: tabNavigatePath(tab),
        search: tab.search || '',
      })
      return
    }
    history.push(tabNavigatePath(tab))
  }

  return (
    <Root
      id={id}
      className={className}
      orientation="horizontal"
      value={activeValue ?? null}
      onValueChange={handleValueChange}
    >
      <StyledTabsList id={`${id}-list`} $variant={variant}>
        {visibleTabs.map((tab) => (
          <TabItem
            key={tab.value}
            id={id}
            variant={variant}
            size={size}
            item={{
              value: tab.value,
              disabled: tab.disabled,
              label:
                tab.badge !== undefined && tab.badge !== null
                  ? `${tab.label} · ${tab.badge}`
                  : tab.label,
            }}
          />
        ))}
      </StyledTabsList>
    </Root>
  )
}

export default PageTabs
